import { Typography, type TypographyProps } from '@mui/material'

// SSR-safe equivalent of the DOM-based sanitizeHtml reference.
// Keeps only <a> tags (href only, scheme-validated). All other tags are
// stripped (wrapper removed, text content preserved). Browser-unsafe schemes
// like javascript: and data: are dropped, leaving a bare <a> with no href.
const SAFE_URL_PATTERN = /^(https?:|mailto:|tel:|\/|#)/i

function safeHref(value: string): string | null {
    const trimmed = value.trim()
    if (trimmed === '') return null
    return SAFE_URL_PATTERN.test(trimmed) ? trimmed : null
}

export function sanitizeRichText(html: string): string {
    if (!html) return ''
    // Remove all tags that are not <a> or </a>, keeping their text content
    let sanitized = html.replace(/<(?!\/?a[\s>\/])[^>]*>/gi, '')
    // On <a> tags keep only a scheme-validated href — strip everything else
    sanitized = sanitized.replace(/<a\b([^>]*)>/gi, (_match, attrs) => {
        const raw =
            /href="([^"]*)"/i.exec(attrs)?.[1] ??
            /href='([^']*)'/i.exec(attrs)?.[1] ??
            /href=([^\s>"']+)/i.exec(attrs)?.[1] ??
            ''
        const href = safeHref(raw)
        return href !== null ? `<a href="${href}">` : '<a>'
    })
    return sanitized
}

interface RichTextProps extends Omit<TypographyProps, 'children' | 'dangerouslySetInnerHTML'> {
    html: string
}

export default function RichText({ html, sx, ...props }: RichTextProps) {
    return (
        <Typography
            {...props}
            sx={[{ '& a': { cursor: 'pointer', textDecoration: 'none', color: 'primary.main' } }, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
            dangerouslySetInnerHTML={{ __html: sanitizeRichText(html) }}
        />
    )
}
